package com.essntl.features.client.ui.addeditclient

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import com.essntl.features.client.ui.addeditclient.components.ClientBaseInformation
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.domain.model.shareLink
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.shareLink
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.card.EsExpandableCardsSection
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.primary.EsSmallPrimaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDateRange

@Composable
fun AddEditClientScreen(
    state: AddEditClientState,
    onEvent: (AddEditClientEvent) -> Unit,
    onNavigation: (AddEditClientNavigation) -> Unit = {},
    validation: AddEditClientValidation,
    dialog: Boolean = false,
) {
    val isLoading = remember(state) { state.isLoading }

    val isFailure = remember(state) { state.isFailure }

    val isEdit by remember(state.client.value.id) {
        mutableStateOf(
            state.client.value.id.isNotBlank(),
        )
    }

    val uriHandler = LocalUriHandler.current

    var isEditEnabled by remember(state.getClient.data?.id) { mutableStateOf(false) }

    LaunchedEffect(state.getClient.data) {
        validation.addEditClientForm.reset()
        validation.addEditClientForm.lastName.value = state.getClient.data?.lastName.orEmpty()
    }

    LaunchedEffect(state.addEditClient.isSuccess) {
        if (state.addEditClient.isSuccess) {
            isEditEnabled = false
        }
    }

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier =
            Modifier
                .fillMaxSize(),
    ) {
        Spacer(modifier = Modifier.height(1f.spacing))

        Box(
            modifier =
                Modifier
                    .weight(1f),
        ) {
            val lazyListState = rememberLazyListState()

            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(1f.spacing),
                state = lazyListState,
                modifier =
                    Modifier
                        .fillMaxSize()
                        .clip(
                            RoundedCornerShape(
                                topStart = 24.dp,
                                topEnd = 24.dp,
                            ),
                        ),
            ) {
                if (
                    isLoading ||
                    isFailure ||
                    (isEdit && state.getClient.data == null)
                ) {
                    item {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .height(200.dp)
                                    .padding(2.5f.spacing)
                                    .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                        ) {
                            if (isLoading) {
                                EsSpinner()
                            } else {
                                Text(
                                    text = "Something goes wrong!",
                                    color = MaterialTheme.esColorScheme.textSecondary,
                                    style = MaterialTheme.esTypography.p1Medium16,
                                )
                            }
                        }
                    }
                } else {
                    item {
                        ClientBaseInformation(
                            state = state,
                            validation = validation,
                            onClientChange = { onEvent(AddEditClientEvent.UpdateClientFields(it)) },
                            enabled = if (isEdit) isEditEnabled else true,
                        )
                    }

                    if (!dialog) {
                        val proposals =
                            state.getProposals.data.orEmpty().filter {
                                it.status != ProposalModel.Status.Confirmed
                            }

                        if (proposals.isNotEmpty()) {
                            item {
                                EsExpandableCardsSection<ProposalModel>(
                                    items = proposals,
                                    title = "Proposals",
                                    cardTitle = { it.title ?: "" },
                                    cardSubtitle = {
                                        listOf(
                                            it.fromDate,
                                            it.toDate,
                                        ).formatDateRange() + '\n' + rememberNumberOfDaysPrettyString(
                                            it.fromDate,
                                            it.toDate,
                                        )
                                    },
                                    cardActionButtons = {
                                        EsSmallOutlineIconButton(
                                            onClick = {
                                                uriHandler.openUri(
                                                    uri = it.shareLink,
                                                )
                                            },
                                            iconRes = EsIcons.Browse,
                                        )

                                        EsSmallPrimaryIconButton(
                                            onClick = {
                                                onNavigation(AddEditClientNavigation.EditProposal(it.id))
                                            },
                                            iconRes = EsIcons.Edit,
                                        )
                                    },
                                )
                            }
                        }

                        state.getItineraries.data?.let { itineraries ->
                            if (itineraries.isNotEmpty()) {
                                item {
                                    EsExpandableCardsSection<ItineraryModel>(
                                        items = itineraries,
                                        title = "Itineraries",
                                        cardTitle = { it.proposal?.title ?: "" },
                                        cardSubtitle = {
                                            listOf(
                                                it.proposal?.fromDate,
                                                it.proposal?.toDate,
                                            ).formatDateRange() + '\n' + rememberNumberOfDaysPrettyString(
                                                it.proposal?.fromDate,
                                                it.proposal?.toDate,
                                            )
                                        },
                                        cardActionButtons = {
                                            EsSmallOutlineIconButton(
                                                onClick = {
                                                    it.shareLink?.let { link ->
                                                        uriHandler.openUri(
                                                            uri = link,
                                                        )
                                                    }
                                                },
                                                iconRes = EsIcons.Browse,
                                            )

                                            EsSmallPrimaryIconButton(
                                                onClick = {
                                                    onNavigation(AddEditClientNavigation.EditItinerary(it.id))
                                                },
                                                iconRes = EsIcons.Edit,
                                            )
                                        },
                                    )
                                }
                            }
                        }
                    }
                }
            }

            EsVerticalScrollbar(
                lazyListState = lazyListState,
                modifier =
                    Modifier
                        .align(Alignment.CenterEnd)
                        .fillMaxHeight(),
            )
        }

        Spacer(modifier = Modifier.height(1.dp))

        Box(
            modifier =
                Modifier
                    .background(MaterialTheme.esColorScheme.bgSecondary)
                    .padding(2.5.spacing),
        ) {
            EsLargePrimaryButton(
                onClick = {
                    if (isEdit && !isEditEnabled) {
                        isEditEnabled = true
                    } else if (validation.addEditClientForm.validate()) {
                        if (isEdit) {
                            onEvent(AddEditClientEvent.EditClient)
                        } else {
                            onEvent(AddEditClientEvent.AddClient)
                        }
                    }
                },
                text =
                    if (isEdit) {
                        if (isEditEnabled) {
                            "Save changes"
                        } else {
                            "Edit"
                        }
                    } else {
                        "Confirm"
                    },
                loading = state.addEditClient.isLoading,
                enabled = if (isEditEnabled)
                    state.isEditEnabled && validation.addEditClientForm.isValid else
                    true,
                modifier =
                    Modifier
                        .fillMaxWidth(),
            )
        }
    }
}
